import { Scope, VariableEdit } from 'src/components/formComposed/WorkspaceVariablesForm/types';
import { VARIABLE_NAME_REGEX } from 'src/components/formComposed/WorkspaceVariablesForm/util';
import {
  CsvImportGroups,
  CSVVarHeaders,
  CsvVariable,
  CsvWorkspaceGroups,
} from 'src/components/VariableCSVReader/types';
import { getStandardVariableNames } from 'src/components/VariableCSVReader/util';
import { isValidNumberValue } from 'src/utils/csv';
import * as Yup from 'yup';

export const filterCsvVariablesByGroups = (
  variables: CsvVariable[],
  groups: CsvImportGroups | undefined,
  workspaceGroups: CsvWorkspaceGroups | undefined,
): CsvVariable[] => {
  const excludedNames = new Set<string>();

  if (groups) {
    Object.values(groups).forEach((group) => {
      if (group.action === 'exclude') {
        group.variables.forEach((v: CsvVariable) => excludedNames.add(v.name));
      }
    });
  }

  if (workspaceGroups) {
    Object.values(workspaceGroups).forEach((group) => {
      if (group.action === 'exclude') {
        group.variables.forEach((v: CsvVariable) => excludedNames.add(v.name));
      }
    });
  }

  return variables.filter((v) => !excludedNames.has(v.name));
};

export const csvToVariableEdit = (
  csvVariables: CsvVariable[],
  scope: Scope,
  wVars: VariableEdit[],
  originalVariables: VariableEdit[],
): VariableEdit[] => {
  return csvVariables.map((csvVar) => {
    const workspaceVar = wVars?.find((wv) => wv.name === csvVar.name);
    const existsInWorkspaceVars = !!workspaceVar;
    const existingAssessmentVar = originalVariables.find((v) => v.name === csvVar.name);

    if (scope.type === 'assessment') {
      if (existingAssessmentVar) {
        return {
          ...existingAssessmentVar,
          value: csvVar.value ?? 0,
          currency: csvVar.currency,
          _isFromCSV: true,
        };
      } else if (existsInWorkspaceVars && workspaceVar) {
        return {
          ...workspaceVar,
          id: Date.now() + Math.random(),
          value: csvVar.value ?? 0,
          currency: csvVar.currency,
          workspace_variable: false,
          isGlobal: false,
          isInUse: [],
          _isNewOverwrite: true,
          _isFromCSV: true,
        };
      }
    }

    if (scope.type === 'workspace') {
      const existingVar = originalVariables.find((v) => v.name === csvVar.name);
      if (existingVar) {
        return {
          ...existingVar,
          value: csvVar.value ?? 0,
          currency: csvVar.currency,
          _isFromCSV: true,
        };
      }
    }

    return {
      id: Date.now() + Math.random(),
      name: csvVar.name,
      value: csvVar.value ?? 0,
      currency: csvVar.currency,
      workspace_variable: scope.type === 'workspace',
      isGlobal: false,
      isInUse: [],
      alias: null,
      _isNew: true,
      _isFromCSV: true,
    };
  });
};

const standardVariableNames = getStandardVariableNames();

export const csvVariableSchema = Yup.object({
  [CSVVarHeaders.Name]: Yup.string()
    .required('Name is required')
    .test('min-length', 'Name is too short', (value) => !!value && value.trim().length >= 1)
    .matches(VARIABLE_NAME_REGEX, 'Contains unsupported characters'),
  [CSVVarHeaders.Value]: Yup.string().when(CSVVarHeaders.Name, {
    is: (name: string) => standardVariableNames.includes(name?.trim()),
    then: (schema) =>
      schema.test('is-valid-number-or-empty', 'Value must be a valid number', (value) => isValidNumberValue(value)),
    otherwise: (schema) =>
      schema.required('Value is required').test('is-valid-number', 'Value must be a valid number', (value) => {
        if (value === undefined || value === null || value === '') return false;
        return isValidNumberValue(value);
      }),
  }),
  [CSVVarHeaders['Currency (yes/no)']]: Yup.string().when(CSVVarHeaders.Name, {
    is: (name: string) => !standardVariableNames.includes(name?.trim()),
    then: (schema) =>
      schema.required('Currency field is required').oneOf(['yes', 'no'], 'Currency must be "yes" or "no"'),
    otherwise: (schema) => schema.optional(),
  }),
});
